import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { BiathlonEvent, Participant } from '../types/api';
import { fetchStartlistData } from '../services/eqTimingApi';

interface BiathlonEventContextType {
  selectedEvent: BiathlonEvent | null;
  setSelectedEvent: (event: BiathlonEvent | null) => void;
  athletes: Participant[];
  teams: string[];
  clubs: string[];
  kretser: string[];
  classes: string[];
  loading: boolean;
  error: string | null;
  selectedClubs: string[];
  setSelectedClubs: (clubs: string[]) => void;
  selectedTeams: string[];
  setSelectedTeams: (teams: string[]) => void;
  selectedKretser: string[];
  setSelectedKretser: (kretser: string[]) => void;
  selectedClasses: string[];
  setSelectedClasses: (classes: string[]) => void;
  nameFilter: string;
  setNameFilter: (name: string) => void;
  excludeBibs: string;
  setExcludeBibs: (bibs: string) => void;
  startTimeFrom: string;
  setStartTimeFrom: (time: string) => void;
  startTimeTo: string;
  setStartTimeTo: (time: string) => void;
  refreshAthleteData: () => void;
  clearAllAthleteCache: () => void;
}

const extractUniqueClubs = (participants: Participant[]): string[] => {
  const clubs = participants.map(p => p.Utover.Klubb.Navn);
  return Array.from(new Set(clubs)).sort();
};

const extractUniqueClasses = (participants: Participant[]): string[] => {
  const classes = participants.map(p => p.Klasse.Navn);
  return Array.from(new Set(classes)).sort();
};

const extractUniqueTeams = (participants: Participant[]): string[] => {
  const teams = participants
    .map(p => p.Utover.Team?.Navn)
    .filter((team): team is string => team !== undefined);
  return Array.from(new Set(teams)).sort();
};

const extractUniqueKretser = (participants: Participant[]): string[] => {
  const kretser = participants
    .map(p => p.Region?.Navn)
    .filter((krets): krets is string => krets !== undefined && krets !== '');
  return Array.from(new Set(kretser)).sort();
};

const BiathlonEventContext = createContext<BiathlonEventContextType | undefined>(undefined);

export const useBiathlonEvent = () => {
  const context = useContext(BiathlonEventContext);
  if (context === undefined) {
    throw new Error('useBiathlonEvent must be used within a BiathlonEventProvider');
  }
  return context;
};

interface BiathlonEventProviderProps {
  children: ReactNode;
}

export const BiathlonEventProvider: React.FC<BiathlonEventProviderProps> = ({ children }) => {
  const [selectedEvent, setSelectedEventState] = useState<BiathlonEvent | null>(null);
  const [athletes, setAthletes] = useState<Participant[]>([]);
  const [teams, setTeams] = useState<string[]>([]);
  const [clubs, setClubs] = useState<string[]>([]);
  const [kretser, setKretser] = useState<string[]>([]);
  const [classes, setClasses] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedClubsState, setSelectedClubsState] = useState<string[]>([]);
  const [selectedTeamsState, setSelectedTeamsState] = useState<string[]>([]);
  const [selectedKretserState, setSelectedKretserState] = useState<string[]>([]);
  const [selectedClassesState, setSelectedClassesState] = useState<string[]>([]);
  const [nameFilterState, setNameFilterState] = useState<string>('');
  const [excludeBibsState, setExcludeBibsState] = useState<string>('');
  const [startTimeFromState, setStartTimeFromState] = useState<string>('');
  const [startTimeToState, setStartTimeToState] = useState<string>('');
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  useEffect(() => {
    try {
      const storedFilters = localStorage.getItem('filterStates');
      if (storedFilters) {
        const filters = JSON.parse(storedFilters);
        setSelectedClubsState(filters.selectedClubs || []);
        setSelectedTeamsState(filters.selectedTeams || []);
        setSelectedKretserState(filters.selectedKretser || []);
        setSelectedClassesState(filters.selectedClasses || []);
        setNameFilterState(filters.nameFilter || '');
        setExcludeBibsState(filters.excludeBibs || '');
        setStartTimeFromState(filters.startTimeFrom || '');
        setStartTimeToState(filters.startTimeTo || '');
      }

      const storedEventId = localStorage.getItem('selectedEventId');
      if (storedEventId) {
        import('../services/eqTimingApi').then(({ fetchBiathlonEvents, getNext30Events }) => {
          fetchBiathlonEvents()
            .then(allEvents => {
              const next30Events = getNext30Events(allEvents);
              const storedEvent = next30Events.find((event: any) => event.Id.toString() === storedEventId);
              if (storedEvent) {
                setSelectedEventState(storedEvent);
              } else {
                localStorage.removeItem('selectedEventId');
              }
              setIsInitialLoadComplete(true);
            })
            .catch(error => {
              console.error('Error loading stored event:', error);
              localStorage.removeItem('selectedEventId');
              setIsInitialLoadComplete(true);
            });
        });
      } else {
        setIsInitialLoadComplete(true);
      }
    } catch (error) {
      console.error('Error loading persisted data:', error);
      setIsInitialLoadComplete(true);
    }
  }, []);

  useEffect(() => {
    if (!isInitialLoadComplete) {
      return;
    }
    
    try {
      const filterStates = {
        selectedClubs: selectedClubsState,
        selectedTeams: selectedTeamsState,
        selectedKretser: selectedKretserState,
        selectedClasses: selectedClassesState,
        nameFilter: nameFilterState,
        excludeBibs: excludeBibsState,
        startTimeFrom: startTimeFromState,
        startTimeTo: startTimeToState
      };
      localStorage.setItem('filterStates', JSON.stringify(filterStates));
    } catch (error) {
      console.error('Error saving filter states:', error);
    }
  }, [selectedClubsState, selectedTeamsState, selectedKretserState, selectedClassesState, nameFilterState, excludeBibsState, startTimeFromState, startTimeToState, isInitialLoadComplete]);

  const setSelectedEvent = (event: BiathlonEvent | null) => {
    setSelectedEventState(event);
    try {
      if (event) {
        localStorage.setItem('selectedEventId', event.Id.toString());
      } else {
        localStorage.removeItem('selectedEventId');
      }
    } catch (error) {
      console.error('Error saving selected event:', error);
    }
  };

  const setSelectedClubs = (clubs: string[]) => {
    setSelectedClubsState(clubs);
  };

  const setSelectedTeams = (teams: string[]) => {
    setSelectedTeamsState(teams);
  };

  const setSelectedKretser = (kretser: string[]) => {
    setSelectedKretserState(kretser);
  };

  const setSelectedClasses = (classes: string[]) => {
    setSelectedClassesState(classes);
  };

  const setNameFilter = (name: string) => {
    setNameFilterState(name);
  };

  const setExcludeBibs = (bibs: string) => {
    setExcludeBibsState(bibs);
  };

  const setStartTimeFrom = (time: string) => {
    setStartTimeFromState(time);
  };

  const setStartTimeTo = (time: string) => {
    setStartTimeToState(time);
  };

  const refreshAthleteData = () => {
    if (selectedEvent) {
      const cacheKey = `athleteData_${selectedEvent.Id}`;
      localStorage.removeItem(cacheKey);
      setSelectedEventState({ ...selectedEvent });
    }
  };

  const clearAllAthleteCache = () => {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('athleteData_')) {
        localStorage.removeItem(key);
      }
    });
  };

  useEffect(() => {
    const loadAthletes = async () => {
      if (!selectedEvent) {
        setAthletes([]);
        setTeams([]);
        setClubs([]);
        setKretser([]);
        setClasses([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const cacheKey = `athleteData_${selectedEvent.Id}`;
        const cachedData = localStorage.getItem(cacheKey);
        
        let participantArray: Participant[];
        
        if (cachedData) {
          const parsedCache = JSON.parse(cachedData);
          participantArray = parsedCache.athletes;
          
          console.log('=== ATHLETE DATA LOADED FROM CACHE ===');
          console.log('Event ID:', selectedEvent.Id);
          console.log('Total athletes:', participantArray.length);
          console.log('Cache timestamp:', new Date(parsedCache.timestamp).toLocaleString());
          console.log('=====================================');
        } else {
          const data = await fetchStartlistData(selectedEvent.Id);
          participantArray = Object.values(data.Items);
          
          const cacheData = {
            athletes: participantArray,
            timestamp: Date.now(),
            eventId: selectedEvent.Id
          };
          
          try {
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            console.log('=== ATHLETE DATA LOADED FROM API ===');
            console.log('Event ID:', selectedEvent.Id);
            console.log('Total athletes:', participantArray.length);
            console.log('Data cached for future use');
            console.log('===================================');
          } catch (error) {
            console.warn('Failed to cache athlete data to localStorage (quota exceeded):', error);
            console.log('=== ATHLETE DATA LOADED FROM API ===');
            console.log('Event ID:', selectedEvent.Id);
            console.log('Total athletes:', participantArray.length);
            console.log('Data NOT cached due to storage quota');
            console.log('===================================');
          }
        }
        
        setAthletes(participantArray);
        const uniqueTeams = extractUniqueTeams(participantArray);
        const uniqueClubs = extractUniqueClubs(participantArray);
        const uniqueKretser = extractUniqueKretser(participantArray);
        const uniqueClasses = extractUniqueClasses(participantArray);
        
        console.log('Teams list:', uniqueTeams);
        console.log('Clubs list:', uniqueClubs);
        console.log('Kretser list:', uniqueKretser);
        console.log('Classes list:', uniqueClasses);
        
        setTeams(uniqueTeams);
        setClubs(uniqueClubs);
        setKretser(uniqueKretser);
        setClasses(uniqueClasses);
      } catch (err) {
        setError('Failed to load athlete data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadAthletes();
  }, [selectedEvent]);

  const value = {
    selectedEvent,
    setSelectedEvent,
    athletes,
    teams,
    clubs,
    kretser,
    classes,
    loading,
    error,
    selectedClubs: selectedClubsState,
    setSelectedClubs,
    selectedTeams: selectedTeamsState,
    setSelectedTeams,
    selectedKretser: selectedKretserState,
    setSelectedKretser,
    selectedClasses: selectedClassesState,
    setSelectedClasses,
    nameFilter: nameFilterState,
    setNameFilter,
    excludeBibs: excludeBibsState,
    setExcludeBibs,
    startTimeFrom: startTimeFromState,
    setStartTimeFrom,
    startTimeTo: startTimeToState,
    setStartTimeTo,
    refreshAthleteData,
    clearAllAthleteCache,
  };

  return (
    <BiathlonEventContext.Provider value={value}>
      {children}
    </BiathlonEventContext.Provider>
  );
};
